import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../services/api";

/**
 * Custom hook for billing-related operations
 * Extracts all business logic from BillingsTab component
 */
const useBillingHandlers = (job, onRefreshJob) => {
  const navigate = useNavigate();
  const [generatingExcel, setGeneratingExcel] = useState(null);
  const [revising, setRevising] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    method: "",
    notes: ""
  });
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Mark-as-Sent dialog state
  const [markSentDialogOpen, setMarkSentDialogOpen] = useState(false);
  const [markSentForm, setMarkSentForm] = useState({
    date: new Date().toISOString().split("T")[0],
    time: "",
  });
  const [markingSent, setMarkingSent] = useState(false);

  // Navigation handlers
  const handleAddBilling = () => {
    if (!job?.id) return;
    navigate(`/jobs/${job.id}/billing`);
  };

  const handleEditBilling = (billingId) => {
    if (!job?.id) return;
    navigate(`/jobs/${job.id}/billing?billingId=${billingId}`);
  };

  // Excel download
  const downloadExcel = async (billing) => {
    if (!job?.id || !billing?.id) return;
    setGeneratingExcel(billing.id);
    try {
      const response = await apiClient.get(
        `/jobs/${job.id}/billings/${billing.id}/excel`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `G702-G703_${(job?.name || "Job").replace(/[^a-zA-Z0-9]/g, "_")}_App${billing.billing_number}${billing.status === "void" ? "_VOID" : ""}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel file downloaded");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    } finally {
      setGeneratingExcel(null);
    }
  };

  // Revision handler
  const handleReviseBilling = async (billing) => {
    if (!job?.id || !billing?.id) return;
    setRevising(billing.id);
    try {
      await apiClient.post(
        `/jobs/${job.id}/billings/${billing.id}/revise`,
        {}
      );
      toast.success(`Application #${billing.billing_number} voided. Revision created.`);
      if (onRefreshJob) {
        onRefreshJob();
      }
    } catch (error) {
      console.error("Error revising billing:", error);
      toast.error(error.response?.data?.detail || "Failed to revise billing");
    } finally {
      setRevising(null);
    }
  };

  // Payment dialog handlers
  const openPaymentDialog = (billing) => {
    setSelectedBilling(billing);
    const amountDue = (billing.current_payment_due || billing.amount || 0) - (billing.amount_received || 0);
    setPaymentForm({
      amount: amountDue > 0 ? amountDue.toFixed(2) : "",
      date: new Date().toISOString().split("T")[0],
      method: "",
      notes: ""
    });
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedBilling || !paymentForm.amount || !job?.id) return;
    
    setRecordingPayment(true);
    try {
      await apiClient.post(
        `/jobs/${job.id}/billings/${selectedBilling.id}/payment`,
        {
          amount: parseFloat(paymentForm.amount),
          date: paymentForm.date,
          method: paymentForm.method || null,
          notes: paymentForm.notes || null
        }
      );
      toast.success("Payment recorded successfully");
      setPaymentDialogOpen(false);
      if (onRefreshJob) {
        onRefreshJob();
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeletePayment = async (billingId, paymentId) => {
    if (!job?.id) return;
    try {
      await apiClient.delete(
        `/jobs/${job.id}/billings/${billingId}/payment/${paymentId}`
      );
      toast.success("Payment deleted");
      if (onRefreshJob) {
        onRefreshJob();
      }
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  // Mark-as-Sent handlers
  const openMarkSentDialog = (billing, { useNow = false } = {}) => {
    setSelectedBilling(billing);
    // Pre-fill with today's date & current local time (HH:mm)
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const nowTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setMarkSentForm({
      date: useNow ? today : billing.date_sent || today,
      time: useNow ? nowTime : billing.time_sent || nowTime,
    });
    setMarkSentDialogOpen(true);
  };

  const handleMarkSent = async () => {
    if (!selectedBilling || !markSentForm.date || !job?.id) return;
    setMarkingSent(true);
    try {
      await apiClient.put(
        `/jobs/${job.id}/billings/${selectedBilling.id}`,
        {
          date_sent: markSentForm.date,
          time_sent: markSentForm.time || null,
          payment_status: "sent",
        }
      );
      toast.success(`Application #${selectedBilling.billing_number} marked as sent`);
      setMarkSentDialogOpen(false);
      if (onRefreshJob) onRefreshJob();
    } catch (error) {
      console.error("Error marking billing as sent:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as sent");
    } finally {
      setMarkingSent(false);
    }
  };

  // Sort and calculate billings - guard against null/undefined job
  const billings = job?.billings || [];
  const sortedBillings = [...billings].sort((a, b) => {
    if (a.status === "void" && b.status !== "void") return 1;
    if (a.status !== "void" && b.status === "void") return -1;
    const aNum = parseInt(String(a.billing_number || "0").split("-")[0] || "0") || 0;
    const bNum = parseInt(String(b.billing_number || "0").split("-")[0] || "0") || 0;
    return aNum - bNum;
  });

  const activeBillings = sortedBillings.filter(b => b.status !== "void");
  const totalBilled = activeBillings.reduce((sum, b) => sum + (b.current_payment_due || b.amount || 0), 0);
  const totalReceived = activeBillings.reduce((sum, b) => sum + (b.amount_received || 0), 0);
  const totalOutstanding = totalBilled - totalReceived;

  return {
    // State
    generatingExcel,
    revising,
    paymentDialogOpen,
    setPaymentDialogOpen,
    selectedBilling,
    paymentForm,
    setPaymentForm,
    recordingPayment,
    markSentDialogOpen,
    setMarkSentDialogOpen,
    markSentForm,
    setMarkSentForm,
    markingSent,

    // Calculated values
    sortedBillings,
    activeBillings,
    totalBilled,
    totalReceived,
    totalOutstanding,

    // Handlers
    handleAddBilling,
    handleEditBilling,
    downloadExcel,
    handleReviseBilling,
    openPaymentDialog,
    handleRecordPayment,
    handleDeletePayment,
    openMarkSentDialog,
    handleMarkSent,
  };
};

export default useBillingHandlers;
